import { create } from "zustand";
import { persist } from "zustand/middleware";

// ============================================================
// Overlay Depth Management (UXR14)
// ============================================================

type ActiveLevel1 = "none" | "command-palette" | "modal" | "slide-over";

interface Level1Meta {
  id: string;
  title: string;
}

// ============================================================
// UI Store — Global UI State
// ============================================================

interface UIStore {
  // Side Nav (Story 13.2)
  sideNavCollapsed: boolean;
  toggleSideNav: () => void;
  setSideNavCollapsed: (collapsed: boolean) => void;

  // Density
  density: "compact" | "default" | "comfortable";
  setDensity: (density: "compact" | "default" | "comfortable") => void;
  cycleDensity: () => void;

  // Theme
  theme: "dark" | "light";
  toggleTheme: () => void;

  // Overlay Depth (Story 13.3 — AC-8)
  activeLevel1: ActiveLevel1;
  level1Meta: Level1Meta | null;
  commandPaletteOpen: boolean;

  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  openModal: (id: string, title: string) => void;
  closeModal: () => void;
  openSlideOver: (id: string, title: string) => void;
  closeSlideOver: () => void;

  // Legacy compat — kept for existing consumers
  sideNavExpanded: boolean;
  setCommandPaletteOpen: (open: boolean) => void;
}

const DENSITY_CYCLE: Array<"compact" | "default" | "comfortable"> = [
  "compact",
  "default",
  "comfortable",
];

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      // ── Side Nav ──
      sideNavCollapsed: false,
      sideNavExpanded: true,
      toggleSideNav: () =>
        set((state) => ({
          sideNavCollapsed: !state.sideNavCollapsed,
          sideNavExpanded: state.sideNavCollapsed,
        })),
      setSideNavCollapsed: (collapsed) =>
        set({ sideNavCollapsed: collapsed, sideNavExpanded: !collapsed }),

      // ── Density ──
      density: "default",
      setDensity: (density) => set({ density }),
      cycleDensity: () => {
        const current = get().density;
        const idx = DENSITY_CYCLE.indexOf(current);
        const next = DENSITY_CYCLE[(idx + 1) % DENSITY_CYCLE.length];
        set({ density: next });
      },

      // ── Theme ──
      theme: "dark",
      toggleTheme: () =>
        set((state) => {
          const next = state.theme === "dark" ? "light" : "dark";
          if (typeof document !== "undefined") {
            document.documentElement.classList.toggle(
              "light",
              next === "light",
            );
          }
          return { theme: next };
        }),

      // ── Overlay Depth ──
      activeLevel1: "none",
      level1Meta: null,
      commandPaletteOpen: false,

      openCommandPalette: () => {
        set({
          activeLevel1: "command-palette",
          level1Meta: null,
          commandPaletteOpen: true,
        });
      },

      closeCommandPalette: () => {
        const state = get();
        if (state.activeLevel1 === "command-palette") {
          set({
            activeLevel1: "none",
            level1Meta: null,
            commandPaletteOpen: false,
          });
        }
      },

      // Legacy compat
      setCommandPaletteOpen: (open) => {
        if (open) {
          get().openCommandPalette();
        } else {
          get().closeCommandPalette();
        }
      },

      openModal: (id, title) => {
        set({
          activeLevel1: "modal",
          level1Meta: { id, title },
          commandPaletteOpen: false,
        });
      },

      closeModal: () => {
        const state = get();
        if (state.activeLevel1 === "modal") {
          set({ activeLevel1: "none", level1Meta: null });
        }
      },

      openSlideOver: (id, title) => {
        set({
          activeLevel1: "slide-over",
          level1Meta: { id, title },
          commandPaletteOpen: false,
        });
      },

      closeSlideOver: () => {
        const state = get();
        if (state.activeLevel1 === "slide-over") {
          set({ activeLevel1: "none", level1Meta: null });
        }
      },
    }),
    {
      name: "ztd:ui-preferences",
      partialize: (state) => ({
        sideNavCollapsed: state.sideNavCollapsed,
        density: state.density,
        theme: state.theme,
      }),
    },
  ),
);
